import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CreditCard, Building2, CheckCircle2, RefreshCw, TrendingUp, Clock, Layers } from "lucide-react";
import portal from "@/lib/portalApi";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/components/portal/portalConstants";
import { toast } from "sonner";

const STATUS_STYLE = {
  paid: "bg-green-500/15 text-green-300 border-green-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
  expired: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  refunded: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};

const SYM = { eur: "€", usd: "$", aoa: "Kz " };
const fmtCur = (cur, amt) =>
  cur === "aoa" ? `Kz ${Number(amt).toLocaleString()}` : `${SYM[cur] || ""}${Number(amt).toLocaleString()}`;

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "7 days" },
  { key: "month", label: "30 days" },
  { key: "all", label: "All time" },
];

const CurrencyChips = ({ byCurrency, testid }) => {
  const entries = Object.entries(byCurrency || {});
  if (entries.length === 0) return <span className="text-slate-500 text-sm">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {entries.map(([cur, amt]) => (
        <span key={cur} className="rounded-full bg-venka-blue/15 text-venka-green-light px-2.5 py-0.5 text-sm font-semibold">
          {fmtCur(cur, amt)}
        </span>
      ))}
    </div>
  );
};

export const PortalOrders = () => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        portal.get("/portal/orders").then((r) => r.data),
        portal.get("/portal/orders/summary").then((r) => r.data),
      ]);
      setOrders(Array.isArray(o) ? o : []);
      setSummary(s);
    } catch (e) {
      /* handled globally */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markPaid = async (id) => {
    try {
      await portal.post(`/portal/orders/${id}/mark-paid`);
      toast.success("Marked as paid");
      await load();
    } catch (e) {
      toast.error("Could not update order");
    }
  };

  const money = (o) => fmtCur((o.currency || "eur").toLowerCase(), o.amount);

  return (
    <div className="space-y-6" data-testid="portal-orders-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Package Orders</h1>
          <p className="text-sm text-slate-400">Card payments (Stripe) and Angola bank transfers from the packages campaign.</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-full border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="orders-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {summary && (
        <div className="space-y-4" data-testid="orders-summary">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-400 mr-1">Revenue</span>
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  period === p.key ? "brand-gradient text-white" : "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800"
                }`}
                data-testid={`summary-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="summary-revenue-card">
              <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wide"><TrendingUp className="h-4 w-4 text-venka-green-light" /> Revenue ({PERIODS.find((x) => x.key === period)?.label})</div>
              <div className="mt-3"><CurrencyChips byCurrency={summary.by_period?.[period]?.by_currency} testid="summary-revenue-chips" /></div>
              <p className="mt-2 text-xs text-slate-500">{summary.by_period?.[period]?.count || 0} paid order(s)</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="summary-paid-card">
              <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wide"><CheckCircle2 className="h-4 w-4 text-green-400" /> Paid (all time)</div>
              <p className="mt-3 text-3xl font-bold text-white">{summary.paid_count}</p>
              <div className="mt-2"><CurrencyChips byCurrency={summary.paid_by_currency} testid="summary-total-chips" /></div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="summary-pending-card">
              <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wide"><Clock className="h-4 w-4 text-amber-400" /> Awaiting payment</div>
              <p className="mt-3 text-3xl font-bold text-white">{summary.pending_count}</p>
              <p className="mt-2 text-xs text-slate-500">{summary.total_orders} orders total</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="summary-tier-card">
              <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wide"><Layers className="h-4 w-4 text-venka-green-light" /> By tier (paid)</div>
              <div className="mt-3 space-y-2">
                {Object.entries(summary.by_tier || {}).map(([tier, d]) => (
                  <div key={tier} className="flex items-center justify-between gap-2" data-testid={`summary-tier-${tier}`}>
                    <span className="text-sm text-slate-200">{tier} <span className="text-slate-500">×{d.paid_count}</span></span>
                    <CurrencyChips byCurrency={d.by_currency} />
                  </div>
                ))}
                {Object.keys(summary.by_tier || {}).length === 0 && <span className="text-slate-500 text-sm">—</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-card p-12 text-center text-slate-400" data-testid="orders-empty">
          No orders yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-300 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Package</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Reference</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-white/[0.02]" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-slate-100">{o.customer_name}</p>
                    <p className="text-xs text-slate-500">{o.customer_email}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-200">{o.tier}</td>
                  <td className="px-4 py-3 text-slate-200">{money(o)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-slate-300">
                      {o.method === "bank" ? <Building2 className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                      {o.method === "bank" ? "Bank" : "Card"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{o.reference || (o.session_id ? o.session_id.slice(0, 12) + "…" : "—")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[o.payment_status] || STATUS_STYLE.pending}`}>
                      {o.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{formatDateTime(o.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {o.method === "bank" && o.payment_status !== "paid" && (
                      <Button size="sm" onClick={() => markPaid(o.id)} className="rounded-full brand-gradient text-white" data-testid={`order-mark-paid-${o.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark paid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortalOrders;
